import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, Download, Loader2, Mic } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { Eyebrow, Reveal } from "./Reveal";
import { AttachmentField } from "./AttachmentField";

const TYPES = ["Acting & Casting", "Modeling & Brands", "Activism & Partnerships", "Press", "Other"];
const empty = { name: "", email: "", organization: "", inquiry_type: TYPES[0], message: "" };
const field = "w-full bg-transparent border-0 border-b border-black/20 focus:border-[var(--gold)] focus:outline-none py-4 text-lg text-[var(--ink)] placeholder:text-stone-500 transition-colors";

const AUDITION_MSG = "I'd like to request an audition with Mimi Tao.\n\nProject / role: \nShoot dates & location: \nSides or breakdown link: ";

export const Contact = () => {
  const [params] = useSearchParams();
  const audition = params.get("type") === "casting";
  const [f, setF] = useState(audition ? { ...empty, inquiry_type: TYPES[0], message: AUDITION_MSG } : empty);
  const formRef = useRef(null);
  useEffect(() => {
    if (!audition) return undefined;
    const t = setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 600);
    return () => clearTimeout(t);
  }, [audition]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [attachment, setAttachment] = useState(null);
  const casting = f.inquiry_type === TYPES[0];
  const startAudition = () => {
    setF((v) => ({ ...v, inquiry_type: TYPES[0], message: v.message.trim() ? v.message : AUDITION_MSG }));
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { ...f, attachment_id: casting && attachment ? attachment.id : null });
      setSent(true);
      setF(empty);
      setAttachment(null);
      toast.success("Thank you — your message has been received.");
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="pt-36 sm:pt-44 pb-28">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-5">
          <Eyebrow className="mb-6">05 — Contact</Eyebrow>
          <h2 className="font-display text-5xl sm:text-7xl leading-[0.95] tracking-tight">Let’s make<br /><em className="text-[var(--gold)]">something</em><br />that matters.</h2>
          <p className="mt-8 text-stone-600 font-light leading-relaxed max-w-sm">Casting, brand partnerships, press and advocacy — every message is read personally by Mimi’s team.</p>
          <dl className="mt-12 max-w-sm divide-y divide-black/10 border-y border-black/10 text-sm">
            <div className="flex justify-between gap-6 py-4"><dt className="text-[12px] uppercase tracking-[0.2em] text-stone-500">Email</dt>
              <dd><a data-testid="contact-management-email" href="mailto:mimitao.tmm@gmail.com" className="text-stone-800 link-line">mimitao.tmm@gmail.com</a></dd></div>
            <div className="flex justify-between gap-6 py-4"><dt className="text-[12px] uppercase tracking-[0.2em] text-stone-500">Manager</dt>
              <dd className="text-right space-y-1">
                <a data-testid="contact-manager-email" href="mailto:tammy@sandstoneartists.com" className="block text-stone-800 link-line">tammy@sandstoneartists.com</a>
                <a data-testid="contact-manager-phone" href="tel:+13109909005" className="block text-stone-800 link-line">+1 (310) 990-9005</a>
              </dd></div>
          </dl>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-[12px] uppercase tracking-[0.22em]">
            <a data-testid="contact-press-kit-download" href="/mimi-tao-press-kit.pdf" download="Mimi-Tao-Press-Kit.pdf"
              className="inline-flex items-center gap-2 text-stone-800 hover:text-[var(--gold)] transition-colors duration-300">
              <Download size={14} className="text-[var(--gold)]" /> Press kit
            </a>
            <button type="button" data-testid="contact-request-audition-btn" onClick={startAudition}
              className="inline-flex items-center gap-2 uppercase text-stone-800 hover:text-[var(--gold)] transition-colors duration-300">
              <Mic size={14} className="text-[var(--gold)]" /> Request audition
            </button>
          </div>
        </Reveal>
        <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.1}>
          <form ref={formRef} onSubmit={submit} data-testid="contact-form" className="space-y-6">
            <label className="block">
              <span className="text-[12px] uppercase tracking-[0.2em] text-stone-500">Enquiry</span>
              <select data-testid="contact-type-select" value={f.inquiry_type} onChange={set("inquiry_type")}
                className={`${field} cursor-pointer appearance-none bg-[length:12px] bg-no-repeat bg-[right_4px_center]`}
                style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' fill='none' stroke='%23a07a3c' stroke-width='1.5'/%3E%3C/svg%3E\")" }}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <div className="grid sm:grid-cols-2 gap-6">
              <input data-testid="contact-name-input" required className={field} placeholder="Your name" value={f.name} onChange={set("name")} />
              <input data-testid="contact-email-input" required type="email" className={field} placeholder="Email address" value={f.email} onChange={set("email")} />
            </div>
            <input data-testid="contact-org-input" className={field} placeholder="Organisation (optional)" value={f.organization} onChange={set("organization")} />
            <textarea data-testid="contact-message-input" required rows={casting ? 6 : 4} className={`${field} resize-none`} placeholder="Your message" value={f.message} onChange={set("message")} />
            {casting && <AttachmentField value={attachment} onChange={setAttachment} />}
            <div className="flex items-center justify-between gap-4 pt-4">
              {sent ? (
                <p data-testid="contact-success" className="flex items-center gap-2 text-sm text-[var(--gold)]"><Check size={16} /> Message received.</p>
              ) : <span />}
              <button data-testid="contact-submit-btn" disabled={busy} type="submit"
                className="group inline-flex items-center gap-3 rounded-full bg-[var(--ink)] text-white px-8 py-4 text-[12px] uppercase tracking-[0.22em] hover:bg-[var(--gold)] transition-colors duration-500 disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : null} Send message
                <ArrowRight size={16} className="transition-transform duration-500 group-hover:translate-x-1" />
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
